import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Prestataires.css';
import { Search, Calendar, Download, Plus } from 'lucide-react';
import { FaRegCalendarAlt, FaSearch } from 'react-icons/fa';
import { usePrestataires } from '../hooks/usePrestataires';
import { API_SERVER_URL } from '../config/api';
import ViewToggle from '../components/ViewToggle';

const Prestataires = () => {
  const navigate = useNavigate();
  const { prestataires, loading, error, refetch } = usePrestataires();
  const [filteredPrestataires, setFilteredPrestataires] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('cards');
  const [statusFilter, setStatusFilter] = useState('Tous');
  const [filters, setFilters] = useState({
    dateInscription: '',
    typePrestataire: ''
  });

  useEffect(() => {
    filterPrestataires();
  }, [prestataires, searchQuery, statusFilter, filters]);

  const filterPrestataires = () => {
    let filtered = [...prestataires];

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(p => 
        (p.business_name && p.business_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (p.first_name && p.first_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (p.last_name && p.last_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (p.email && p.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (p.phone && p.phone.includes(searchQuery))
      );
    }

    // Filter by status
    if (statusFilter !== 'Tous') {
      filtered = filtered.filter(p => {
        if (statusFilter === 'Actif') return p.is_active && p.user_is_active;
        if (statusFilter === 'Suspendu') return !p.is_active || !p.user_is_active;
        if (statusFilter === 'En attente de validation') return p.verification_status === 'pending';
        return true;
      });
    }

    setFilteredPrestataires(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const getDisplayName = (prestataire) => {
    if (prestataire.business_name) return prestataire.business_name;
    if (prestataire.first_name && prestataire.last_name) {
      return `${prestataire.first_name} ${prestataire.last_name}`;
    }
    if (prestataire.first_name) return prestataire.first_name;
    return 'Sans nom';
  };

  const getStatus = (prestataire) => {
    if (!prestataire.is_active || !prestataire.user_is_active) return 'Suspendu';
    if (prestataire.verification_status === 'pending') return 'En attente';
    if (prestataire.verification_status === 'verified') return 'Actif';
    return 'Inactif';
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Actif': return 'status-active';
      case 'Suspendu': return 'status-suspended';
      case 'En attente': return 'status-pending';
      default: return 'status-inactive';
    }
  };

  const getProfileType = (prestataire) => {
    return prestataire.profile_type === 'moral' ? 'Entreprise' : 'Particulier';
  };

  const getServiceTags = (prestataire) => {
    // Utiliser les vrais services du prestataire récupérés de la base de données
    if (prestataire.services && Array.isArray(prestataire.services)) {
      return prestataire.services.map(service => service.name || service);
    }
    // Fallback si pas de services
    return [];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR');
    } catch {
      return 'N/A';
    }
  };

  const getImageUrl = (prestataire) => {
    if (prestataire.profile_picture_url) {
      // Si l'URL commence par /uploads, ajouter l'URL du backend
      if (prestataire.profile_picture_url.startsWith('/uploads')) {
        return `${API_SERVER_URL}${prestataire.profile_picture_url}`;
      }
      return prestataire.profile_picture_url;
    }
    return '/avatar.png';
  };

  if (loading) {
    return (
      <div className="prestataires-container">
        <div className="loading">Chargement des prestataires...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="prestataires-container">
        <div className="error">Erreur: {error}</div>
      </div>
    );
  }

  return (
    <div className="prestataires-container">
      <div className="prestataires-header-line">
        <h2 className="prestataires-title">Prestataires</h2>

        <div className="prestataires-filters">
          <div className="date-inscription-container">
            <label htmlFor="date-inscription">Date d'inscription</label>
            <div className="date-input-wrapper">
              <FaRegCalendarAlt className="calendar-icon" />
              <input 
                type="date" 
                id="date-inscription" 
                className="date-input"
                value={filters.dateInscription}
                onChange={(e) => handleFilterChange('dateInscription', e.target.value)}
              />
            </div>
          </div>
          
          <select 
            className="client-type-select"
            value={filters.typePrestataire}
            onChange={(e) => handleFilterChange('typePrestataire', e.target.value)}
          >
            <option value="">Type</option>
            <option value="physique">Particulier</option>
            <option value="moral">Entreprise</option>
          </select>

          <div className="client-status-toggle">
            {[
              { key: 'Tous', label: 'Tous' },
              { key: 'Actif', label: 'Actif' },
              { key: 'Suspendu', label: 'Suspendu' },
              { key: 'En attente de validation', label: 'En attente' }
            ].map(status => (
              <button
                key={status.key}
                className={`status-btn ${statusFilter === status.key ? 'active' : ''}`}
                onClick={() => setStatusFilter(status.key)}
              >
                {status.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="prestataires-search-section">
        <div className="search-wrapper">
          <FaSearch className="search-icon" />
          <input 
            className="search-bar"
            type="text" 
            placeholder="Recherche nom, email, tel ..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="prestataires-actions">
          <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
          <button className="btn-create">
            <Plus size={16} style={{ marginRight: '5px' }} />
            Créer prestataire
          </button>
          <button className="export-btn">Export CSV</button>
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className="prestataires-table-container">
          <table className="prestataires-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Email</th>
                <th>Téléphone</th>
                <th>Type</th>
                <th>Services</th>
                <th>Statut</th>
                <th>Date</th>
                <th>Missions</th>
                <th>Signalements</th>
              </tr>
            </thead>
            <tbody>
              {filteredPrestataires.map((prestataire) => {
                const displayName = getDisplayName(prestataire);
                const status = getStatus(prestataire);
                const statusClass = getStatusClass(status);
                const profileType = getProfileType(prestataire);
                const serviceTags = getServiceTags(prestataire);
                return (
                  <tr key={prestataire.id} onClick={() => navigate(`/prestataires/${prestataire.id}`)} style={{ cursor: 'pointer' }}>
                    <td>
                      <div className="table-name-cell">
                        <div className="avatar-circle table-avatar">
                          <div className="green-logo">
                            {prestataire.logo_url ?
                              <img src={prestataire.logo_url} alt="logo" style={{width: '24px', height: '24px', objectFit: 'contain', borderRadius: '50%'}} /> :
                              <span style={{color: '#10b981', fontSize: '12px', fontWeight: '600'}}>
                                {displayName.split(' ').map(word => word.charAt(0)).join('').substring(0, 2).toUpperCase()}
                              </span>
                            }
                          </div>
                        </div>
                        <span>{displayName}</span>
                      </div>
                    </td>
                    <td>{prestataire.email || '—'}</td>
                    <td>{prestataire.phone || '—'}</td>
                    <td>{profileType}</td>
                    <td>
                      <div className="table-tags">
                        {serviceTags.slice(0, 2).map((tag, i) => (
                          <span key={i} className="service-tag">{tag}</span>
                        ))}
                        {serviceTags.length > 2 && <span className="service-tag">+{serviceTags.length - 2}</span>}
                      </div>
                    </td>
                    <td><span className={`badge-status ${statusClass}`}>{status}</span></td>
                    <td>{formatDate(prestataire.created_at)}</td>
                    <td className="table-stat missions">{prestataire.total_missions || 0}</td>
                    <td className="table-stat reports">{prestataire.total_reports || 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
      <div className="prestataires-grid">
        {filteredPrestataires.map((prestataire) => {
          const displayName = getDisplayName(prestataire);
          const status = getStatus(prestataire);
          const statusClass = getStatusClass(status);
          const profileType = getProfileType(prestataire);
          const serviceTags = getServiceTags(prestataire);
          
          return (
            <div 
              className="card-prestataire" 
              key={prestataire.id}
              onClick={() => navigate(`/prestataires/${prestataire.id}`)}
              style={{ cursor: 'pointer' }}
            >
              {/* Header avec logo, nom, statut et ID */}
              <div className="card-header">
                <div className="left">
                  <div className="avatar-circle">
                    <div className="green-logo">
                      {prestataire.logo_url ?
                        <img src={prestataire.logo_url} alt="logo" style={{width: '36px', height: '36px', objectFit: 'contain', borderRadius: '50%'}} /> :
                        <span style={{color: '#10b981', fontSize: '20px', fontWeight: '600'}}>
                          {displayName.split(' ').map(word => word.charAt(0)).join('').substring(0, 2).toUpperCase()}
                        </span>
                      }
                    </div>
                  </div>
                  <div className="header-info">
                    <h3>
                      {displayName} 
                      <span className={`badge-status ${statusClass}`}>{status}</span>
                    </h3>
                    <p>ID : {prestataire.id.substring(0, 8)}</p>
                    {/* Tags des services en dessous de l'ID */}
                    <div className="service-tags-below">
                      {serviceTags.map((tag, index) => (
                        <span
                          key={index}
                          className="service-tag"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <input type="checkbox" onClick={(e) => e.stopPropagation()} />
              </div>

              {/* Section contact avec photo */}
              <div className="contact-section">
                <div className="contact-info">
                  <div className="avatar-and-name">
                    <div className="top-row">
                      <div className="profile-avatar-container">
                        {prestataire.profile_picture_url ? (
                          <img 
                            src={getImageUrl(prestataire)} 
                            alt="avatar" 
                            className="profile-avatar"
                            onError={(e) => { 
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className="profile-avatar-default" 
                          style={{display: prestataire.profile_picture_url ? 'none' : 'flex'}}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="#9ca3af">
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                          </svg>
                        </div>
                      </div>
                      <span className="contact-name">{displayName}</span>
                    </div>
                    <div className="contact-below-avatar">
                      <span className="contact-email">
                        <img src="/icons/email.png" alt="email" className="contact-icon" />
                        {prestataire.email || 'Email non défini'}
                      </span>
                      <span className="contact-phone">
                        <img src="/icons/phone.png" alt="phone" className="contact-icon" />
                        {prestataire.phone || 'Téléphone non défini'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="validation-date">
                  <span className={`doc-status ${prestataire.verification_status === 'verified' ? 'verified' : 'pending'}`}>
                    {prestataire.verification_status === 'verified' ? '✓ Document validé' : 'En attente'}
                  </span>
                  <span className="date-info">
                    <img src="/icons/calendar.png" alt="date" className="date-icon-img" />
                    {formatDate(prestataire.created_at)}
                  </span>
                </div>
              </div>

              {/* Footer avec statistiques et actions */}
              <div className="card-footer">
                <div className="stats">
                  <span className="stat missions">{prestataire.total_missions || 0} Mission{prestataire.total_missions !== 1 ? 's' : ''} réalisée{prestataire.total_missions !== 1 ? 's' : ''}</span>
                  <span className="stat reports">{prestataire.total_reports || 0} Signalement{(prestataire.total_reports || 0) !== 1 ? 's' : ''} reçu{(prestataire.total_reports || 0) !== 1 ? 's' : ''}</span>
                  <span className="stat commission">{(prestataire.total_commission || 0).toLocaleString()} CFA Commission cumulée</span>
                </div>
                <div className="actions">
                  <div className="actions-left">
                    <button className="csv-btn">
                      <img src="/icons/share.png" alt="csv" className="btn-icon" />
                      CSV
                    </button>
                    <button className="delete-btn">
                      <img src="/icons/trash-2.png" alt="delete" className="btn-icon" />
                    </button>
                  </div>
                  <div className="actions-right">
                    <button className="suspend-btn">
                      Suspendre
                    </button>
                    <button className="validate-btn">
                      Valider
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}

      {filteredPrestataires.length === 0 && (
        <div className="no-results">
          <p>Aucun prestataire trouvé avec les critères de recherche.</p>
        </div>
      )}
    </div>
  );
};

export default Prestataires;